import React, { useMemo, useRef } from "react";

import type { IIssue, ISiteSlim, IResponseWithLoading } from "App/Modules/Models";

import { useTranslation } from "react-i18next";

import type { Column } from "react-table";
import { useTable } from "react-table";
import {
    StyledDateTime,
    DefaultTable,
    getTableHooks,
    getRowId,
    getUserProfile,
    hasUserAccessToMonitoring,
} from "App/ReactComponents/Shared";
import { getIssueAvatar, issueCell, IssueAcknowledgedCell } from "./IssueHelpers";
import type { LocalIssueRowState } from "./IssueTypes";
import { useIssueStyles } from "./issueListStyles";

type Props = {
    issues: IIssue[];
    site: ISiteSlim;
    loadingState: IResponseWithLoading<void>;
    updateIssues: (...issues: Array<Partial<IIssue>>) => void;
};

export const IssuesList = (props: Props) => {
    const profile = getUserProfile();
    const showMonitoring = hasUserAccessToMonitoring(profile);

    const { t } = useTranslation();
    const classes = useIssueStyles();
    const columns = useMemo<Column<IIssue>[]>(
        () => [
            {
                accessor: "issueType",
                Cell: (cell) => getIssueAvatar(cell.row.original, classes),
                width: 58,
                maxWidth: 58,
            },
            {
                accessor: "source",
                Cell: (cell) => issueCell(cell.row.original, classes, t, props.site),
                minWidth: 200,
                width: 400,
            },
            {
                accessor: "isActive",
                minWidth: 180,
                width: 280,
                maxWidth: 280,
                Cell: (cell) => (
                    <IssueAcknowledgedCell
                        issue={cell.row.original}
                        timeZone={props.site.timeZone}
                        updateIssue={(i, state) => updateIssue(i, state)}
                        localState={cell.row.state}
                        loadingState={props.loadingState}
                        t={t}
                    />
                ),
            },
            {
                accessor: "dtIssue",
                Cell: (cell) => <StyledDateTime dateTime={cell.row.original.dtIssue} timeZone={props.site.timeZone} />,
                width: 200,
                maxWidth: 200,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.loadingState],
    );

    const data = useRef<IIssue[]>([]);
    if (props.issues && data.current !== props.issues) {
        data.current = props.issues;
    }

    const instance = useTable<IIssue>(
        {
            columns,
            data: data.current,
            getRowId,
            autoResetRowState: false,
            autoResetPage: false,
            initialState: {
                pageSize: 5,
            },
        },
        ...getTableHooks<IIssue>({ rowState: true, pagination: true }),
    );

    function updateIssue(i: Partial<IIssue>, localRowState: LocalIssueRowState) {
        instance.setRowState([i.id], localRowState);
        props.updateIssues(i);
    }

    return showMonitoring ? (
        <>
            <DefaultTable
                instance={instance}
                disableHeader
                loadingState={props.loadingState}
                rowClassName={(row) => (row.original.isActive ? "" : classes.inactiveIssue)}
                rowsPerPage={[5, 10, 20, 30, 40, 50, { label: "All", value: -1 }]}
            />
        </>
    ) : null;
};
